import "flag-icons/css/flag-icons.min.css";

import Link from "next/link";
import { useRouter } from "next/router";
import styled from "styled-components";

import "react-circular-progressbar/dist/styles.css";
import { SectionContainer } from "../../sharedStyles";

import Spacer, { Axis } from "~/components/Spacer";
import { ViewerPermissions } from "~/contexts/ViewerContext";
import useCurrentViewerPermissions from "~/hooks/useCurrentViewerPermissions";
import BlockedVaspLink from "~/templates/companies/components/VASPDueDiligenceReviewSummary/components/BlockedVaspLink";
import MonitoredVaspLink from "~/templates/companies/components/VASPDueDiligenceReviewSummary/components/MonitoredVaspLink";
import ReviewVaspLink from "~/templates/companies/components/VASPDueDiligenceReviewSummary/components/ReviewVaspLink";
import TrustedVaspLink from "~/templates/companies/components/VASPDueDiligenceReviewSummary/components/TrustedVaspLink";

const Container = styled(SectionContainer)`
  width: 275px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const Title = styled.div`
  font-weight: 500;
  font-size: 12px;
  text-transform: uppercase;
  color: #1f1f1f;
`;

const VaspDueDiligenceReviewContainer = styled.div<{ clickable: boolean }>`
  height: 165px;

  padding: 24px 24px 0px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  border: 1px solid #e5e5e5;
  border-radius: 4px;

  ${(params) =>
    params.clickable &&
    `
    &:hover {
    cursor: pointer;
    box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;
  }
  `}
`;

const LastReviewedText = styled.div`
  padding: 12px 0px;

  font-style: normal;
  font-weight: 700;
  font-size: 10px;
  line-height: 12px;

  color: #000000;
`;

const DottedSeparator = styled.div`
  border: 1px dashed #babcba;
`;

export type VaspDueDiligenceReviewSectionProps = {
  vaspDID: string;
  vaspReviewStatus: "TRUSTED" | "BLOCKED" | "MANUAL" | null;
  reviewedDate: Date | null;
};

const formatDate = (date: Date) => {
  // i.e LAST REVIEWED AUG 2 , 2023
  return `Last reviewed ${date.toLocaleString("default", {
    month: "short",
  })} ${date.getDate()}, ${date.getFullYear()} `;
};

export default function VaspDueDiligenceReviewSection({
  vaspDID,
  vaspReviewStatus = null,
  reviewedDate,
}: VaspDueDiligenceReviewSectionProps) {
  const viewerPermissions = useCurrentViewerPermissions();
  const router = useRouter();
  const canChangeTrust = viewerPermissions.includes(ViewerPermissions["rules.set"]);

  const onClickTrustAction = (event: any) => {
    event.stopPropagation();
    router.push(`/dashboard/companies/${vaspDID}?open_vasp_review=true`);
  };

  const contents = (
    <Container id="VaspDueDiligenceReviewSectionContainer">
      <VaspDueDiligenceReviewContainer clickable={canChangeTrust}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <Title>DUE DILIGENCE</Title>
        </div>

        <Spacer axis={Axis.VERTICAL} size={8} />

        <div>
          {vaspReviewStatus === null && <ReviewVaspLink hasPadding={false} clickable={canChangeTrust} />}
          {vaspReviewStatus === "TRUSTED" && <TrustedVaspLink clickable={canChangeTrust} hasPadding={false} />}
          {vaspReviewStatus === "BLOCKED" && <BlockedVaspLink clickable={canChangeTrust} hasPadding={false} />}
          {vaspReviewStatus === "MANUAL" && <MonitoredVaspLink clickable={canChangeTrust} hasPadding={false} />}
        </div>

        <Spacer axis={Axis.VERTICAL} size={24} />

        <DottedSeparator />
        <LastReviewedText>{reviewedDate ? formatDate(reviewedDate) : "Not reviewed"}</LastReviewedText>
      </VaspDueDiligenceReviewContainer>
    </Container>
  );

  if (canChangeTrust) {
    return <a onClick={onClickTrustAction}>{contents}</a>;
  }

  return contents;
}
